import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_VERSION, BUILD_DATE, COPYRIGHT_YEAR, COMPANY_NAME } from '../config/version';
import {
  ClipboardList, CheckSquare, LogOut, User, Settings,
  Wrench, FileText, Calendar, Users, BarChart, Shield, ShieldCheck,
  FolderKanban, Brain, GraduationCap, FolderOpen, HeartPulse,
  Activity, FileSignature, PhoneCall, LucideIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import api from '../services/api';
import NotificationToggle from '../components/NotificationToggle';
import SiteSelector from '../components/SiteSelector';
import { ThemeToggle } from '../theme/ThemeToggle';
import { QuickLinksSidebar } from '../components/QuickLinksSidebar';
import ToolNav from '../ui/ToolNav';
import { getMySummary } from '../lms/api/me';
import type { MySummary } from '../lms/api/me';
import './ToolHub.css';

// Icon mapping for dynamic icon rendering
const iconMap: Record<string, LucideIcon> = {
  ClipboardList,
  CheckSquare,
  Wrench,
  FileText,
  Calendar,
  Users,
  BarChart,
  Shield,
  // IXVerify's tool mark (design system v2 — Lucide tool-mark table). The
  // `features.icon` value is set to 'ShieldCheck' by
  // backend/db/core/migrations/20260812235500_ixverify_v2_features_row.sql.
  ShieldCheck,
  Settings,
  FolderKanban,
  Brain,
  GraduationCap,
  FolderOpen,
  HeartPulse,
  Activity,
  // Budgets and Contracts' tool mark (not FileText — that's eReg's, see
  // backend/db/core/migrations/20260910190000_contracts_tool.sql).
  FileSignature,
  // The 24-Hour Line's tool mark (features.icon 'PhoneCall', set by the
  // oncall_tool migration; docs/plans/oncall-line-plan.md WP-01).
  PhoneCall
};

interface Tool {
  tool_id: string;
  name: string;
  display_name: string;
  description: string;
  icon: string;
  route: string;
  display_order: number;
  is_available: boolean;
  restricted_by: 'tool' | 'network' | 'site' | 'user' | 'no_entitlement' | null;
  source_level?: 'role' | 'user' | 'site' | 'network' | 'default' | 'feature' | null;
  // New entitlements fields
  source?: 'subscription' | 'trial' | 'manual' | 'promotional' | 'migration' | null;
  expires_at?: string | null;
}


interface ToolCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  available: boolean;
  badge?: string;
  badgeType?: 'trial' | 'promo' | 'soon' | 'inactive' | 'default';
}

const ToolCard: React.FC<ToolCardProps> = ({ title, description, icon, to, available, badge, badgeType }) => {
  const navigate = useNavigate();

  const getBadgeClass = () => {
    if (badgeType === 'soon') return 'badge-soon';
    if (badgeType === 'inactive') return 'badge-inactive';
    if (badgeType === 'trial') return 'badge-trial';
    if (badgeType === 'promo') return 'badge-promo';
    if (!available) return 'badge-soon';
    return '';
  };

  return (
    <div className={`tool-card ${!available ? 'tool-coming-soon' : ''}`}>
      <div className="tool-header">
        <span className="tool-icon">{icon}</span>
        {badge && (
          <span className={`tool-badge ${getBadgeClass()}`}>
            {badge}
          </span>
        )}
        {available && !badge && (
          <span className="tool-badge">Available</span>
        )}
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {available ? (
        <button onClick={() => navigate(to)} className="btn btn-primary">
          Launch Tool
        </button>
      ) : (
        <button className="btn btn-disabled" disabled>
          Coming Soon
        </button>
      )}
    </div>
  );
};

type LmsTileBadge = { text: string; type: 'inactive' | 'default' };

/**
 * The Learning Center tile's badge (docs/plans/lms-plan.md 9.4, D22; WP-48):
 * "{n} overdue" in the amber-red family, else "{n} due" for the open
 * assignments, from GET /api/lms/me/summary; nothing when both are zero.
 */
function lmsTileBadge(summary: MySummary | null | undefined): LmsTileBadge | null {
  const overdue = Number(summary?.overdue) || 0;
  const open = Number(summary?.open) || 0;
  if (overdue > 0) return { text: `${overdue} overdue`, type: 'inactive' };
  if (open > 0) return { text: `${open} due`, type: 'default' };
  return null;
}

const ToolHub: React.FC = () => {
  const { user, isAdmin, currentSite, sitesLoading } = useAuth();
  const navigate = useNavigate();
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [lmsBadge, setLmsBadge] = useState<LmsTileBadge | null>(null);
  const lmsBadgeRequest = useRef(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('quickLinksSidebarCollapsed');
    return saved === 'true';
  });

  useEffect(() => {
    // Wait for sites to load and current site to be set before fetching tools
    if (!sitesLoading && currentSite) {
      fetchTools();
    }
  }, [currentSite, sitesLoading]);

  const fetchTools = async () => {
    setLoading(true);
    // 9.4: the Learning Center badge belongs to one site's answer; a new load (a site switch) drops it.
    const badgeRequest = ++lmsBadgeRequest.current;
    setLmsBadge(null);
    try {
      const response = await api.get('/api/my-tools');
      setTools(response.data);
      // 9.4: the summary is asked for once, only when the lms tool is available, and fails silently.
      const lmsTool = Array.isArray(response.data) ? response.data.find((t: Tool) => t.tool_id === 'lms') : undefined;
      if (lmsTool?.is_available === true) {
        getMySummary()
          .then((summary) => {
            if (badgeRequest === lmsBadgeRequest.current) setLmsBadge(lmsTileBadge(summary));
          })
          .catch(() => {});
      }
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const getIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName];
    if (IconComponent) {
      return <IconComponent size={32} />;
    }
    // Default icon if not found
    return <Wrench size={32} />;
  };

  const getBadgeInfo = (tool: Tool): { text?: string; type?: 'trial' | 'promo' | 'soon' | 'inactive' | 'default' } => {
    if (!tool.is_available) {
      // Tool is globally disabled
      if (tool.restricted_by === 'tool') {
        return { text: 'Coming Soon', type: 'soon' };
      }
      // Tool is disabled at network, site, or user level
      if (tool.restricted_by === 'network' || tool.restricted_by === 'site' || tool.restricted_by === 'user') {
        return { text: 'Inactive', type: 'inactive' };
      }
      // No entitlement exists - tool not granted
      if (tool.restricted_by === 'no_entitlement') {
        return { text: 'Not Available', type: 'inactive' };
      }
      // Fallback
      return { text: 'Not Available', type: 'inactive' };
    }

    // Show trial badge with days remaining
    if (tool.source === 'trial' && tool.expires_at) {
      const daysRemaining = Math.ceil(
        (new Date(tool.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      if (daysRemaining > 0) {
        return { text: `Trial - ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`, type: 'trial' };
      }
    }

    // Show promotional badge
    if (tool.source === 'promotional') {
      if (tool.expires_at) {
        const daysRemaining = Math.ceil(
          (new Date(tool.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );
        if (daysRemaining > 0 && daysRemaining <= 30) {
          return { text: `Promo - ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`, type: 'promo' };
        }
      }
      return { text: 'Promotional', type: 'promo' };
    }

    return {};
  };

  return (
    <div className={`tool-hub ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
      <QuickLinksSidebar onCollapseChange={setSidebarCollapsed} />
      <header className="tool-hub-header">
        <div className="tool-hub-left">
          <div className="tool-hub-logo">
            <img src="/pilllogo.svg" alt="SiteOperations.io" className="logo-image" />
            <span className="logo-text">SiteOperations.io</span>
          </div>
          <span className="tool-hub-divider" aria-hidden="true">|</span>
        </div>

        {/* Cross-tool navigation — inline links (xl+) / "Tools" dropdown below. */}
        <ToolNav />

        <div className="tool-hub-user">
          <span className="user-info">
            <User size={14} />
            <span className="user-details">
              <span className="user-name">{user?.full_name}</span>
              <span className="user-email">{user?.email}</span>
            </span>
          </span>
          <SiteSelector />
          <NotificationToggle />
          <ThemeToggle variant="onColor" />
          {isAdmin && (
            <button onClick={() => navigate('/admin')} className="btn btn-icon">
              <Settings size={14} />
              <span className="btn-text">Admin</span>
            </button>
          )}
          <button onClick={handleSignOut} className="btn btn-icon">
            <LogOut size={14} />
            <span className="btn-text">Sign Out</span>
          </button>
        </div>
      </header>

      <main className="tool-hub-main">
        <div className="tool-hub-container">
          <h1>Your Tools</h1>
          <p className="tool-hub-subtitle">Select a tool to get started</p>

          {loading ? (
            <div className="tools-loading">
              <div className="loading-spinner"></div>
              <p>Loading tools...</p>
            </div>
          ) : tools.length === 0 ? (
            <div className="tools-empty">
              <p>No tools are currently available for your account.</p>
              <p>Please contact your administrator for access.</p>
            </div>
          ) : (
            <div className="tools-grid">
              {tools.map(tool => {
                const badgeInfo = tool.tool_id === 'lms' && tool.is_available && lmsBadge ? lmsBadge : getBadgeInfo(tool);
                return (
                  <ToolCard
                    key={tool.tool_id}
                    title={tool.display_name}
                    description={tool.description}
                    icon={getIcon(tool.icon)}
                    to={tool.route}
                    available={tool.is_available}
                    badge={badgeInfo.text}
                    badgeType={badgeInfo.type}
                  />
                );
              })}
            </div>
          )}
        </div>
      </main>
      <footer className="tool-hub-footer">
        <p>v{APP_VERSION} | {BUILD_DATE} | &copy; {COPYRIGHT_YEAR} {COMPANY_NAME} | <a href="/privacy">Privacy Policy</a></p>
      </footer>
    </div>
  );
};

export default ToolHub;
